import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle, ArrowLeftRight, Link2 } from "lucide-react";

export default function BankReconPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    period: new Date().toISOString().slice(0, 7),
    statement_balance: 0,
    csv: "",
  });

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/bank-recon?property_id=${propertyId}`);
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load reconciliations"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [propertyId]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/bank-recon/${id}`);
      setDetail(data);
    } catch (e) { toast.error("Failed to load detail"); }
  };

  const parseCsv = (text) => {
    const rows = text.trim().split(/\r?\n/).filter(Boolean);
    return rows.map((line) => {
      const parts = line.split(",").map((s) => s.trim());
      return {
        date: parts[0],
        description: parts[1] || "",
        amount: parseFloat(parts[2] || 0),
      };
    }).filter((r) => r.date && !isNaN(r.amount));
  };

  const createRecon = async () => {
    try {
      const bank_transactions = parseCsv(form.csv);
      const { data } = await api.post("/admin/bank-recon", {
        property_id: propertyId,
        period: form.period,
        statement_balance: parseFloat(form.statement_balance || 0),
        bank_transactions,
      });
      toast.success(`Reconciliation ${data.period} created`);
      setCreateOpen(false);
      setForm({ period: new Date().toISOString().slice(0, 7), statement_balance: 0, csv: "" });
      load();
      openDetail(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const match = async (glId, bankId) => {
    if (!detail) return;
    try {
      await api.post(`/admin/bank-recon/${detail.id}/match`, {
        gl_entry_id: glId, bank_txn_id: bankId,
      });
      openDetail(detail.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const unmatch = async (glId) => {
    if (!detail) return;
    try {
      await api.post(`/admin/bank-recon/${detail.id}/unmatch/${glId}`);
      openDetail(detail.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const finalize = async () => {
    if (!detail) return;
    try {
      await api.post(`/admin/bank-recon/${detail.id}/finalize`);
      toast.success("Reconciliation finalized");
      openDetail(detail.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="bank-recon-page">
      <PageHeader
        eyebrow="Financials"
        title="Bank Reconciliation"
        subtitle="Match bank statement transactions to your General Ledger and lock the month."
        actions={
          <Button onClick={() => setCreateOpen(true)} className="gap-2" data-testid="new-recon-btn">
            <Plus size={14} /> New reconciliation
          </Button>
        }
      />

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">Reconciliations</div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Bank</TableHead>
              <TableHead className="text-right">GL</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead>Matches</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm" data-testid="recon-empty">
                No reconciliations yet. Click <em>New reconciliation</em> to start your first monthly close.
              </TableCell></TableRow>
            )}
            {items.map((r) => (
              <TableRow key={r.id} className="border-white/5" data-testid={`recon-row-${r.id}`}>
                <TableCell className="font-medium">{r.period}</TableCell>
                <TableCell className="text-right font-mono">${r.statement_balance.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono">${r.gl_balance.toLocaleString()}</TableCell>
                <TableCell className={`text-right font-mono ${Math.abs(r.variance) > 0.01 ? "text-rose-400" : "text-emerald-500"}`}>
                  ${r.variance.toLocaleString()}
                </TableCell>
                <TableCell className="text-sm">
                  {r.match_count} matched · {r.unmatched_bank_count + r.unmatched_gl_count} unmatched
                </TableCell>
                <TableCell>
                  <Badge variant={r.status === "finalized" ? "default" : "secondary"}>
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => openDetail(r.id)} data-testid={`open-recon-${r.id}`}>Open</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create reconciliation */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl" data-testid="create-recon-dialog">
          <DialogHeader><DialogTitle>New reconciliation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period (YYYY-MM)</Label>
                <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="recon-period" />
              </div>
              <div className="space-y-2">
                <Label>Statement balance ($)</Label>
                <Input type="number" value={form.statement_balance}
                  onChange={(e) => setForm({ ...form, statement_balance: e.target.value })} data-testid="recon-balance" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bank transactions (CSV: date,description,amount per line)</Label>
              <Textarea rows={8} placeholder="2026-02-01,Rent Deposit Unit 4B,2500&#10;2026-02-03,Landscaping ACH,-800"
                value={form.csv} onChange={(e) => setForm({ ...form, csv: e.target.value })} data-testid="recon-csv"
                className="font-mono text-xs" />
              <div className="text-xs text-muted-foreground">Positive = credit (deposit), negative = debit (withdrawal). Bank statement PDF-to-CSV coming in R77.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createRecon} data-testid="recon-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="recon-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Reconciliation · {detail.period} · <span className={Math.abs(detail.variance) > 0.01 ? "text-rose-400" : "text-emerald-500"}>
                    Variance ${detail.variance.toLocaleString()}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                    <ArrowLeftRight size={12} /> GL Entries ({detail.gl_entries?.length || 0})
                  </div>
                  <div className="space-y-1 max-h-96 overflow-y-auto">
                    {(detail.gl_entries || []).map((g) => {
                      const matched = detail.matches.find((m) => m.gl_entry_id === g.gl_entry_id);
                      return (
                        <div key={g.gl_entry_id} className={`text-xs p-2 rounded border ${matched ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10"}`}
                          data-testid={`gl-entry-${g.gl_entry_id}`}>
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-muted-foreground">{g.date}</span> {g.description}
                            </div>
                            <div className={g.amount < 0 ? "text-rose-400 font-mono" : "text-emerald-500 font-mono"}>
                              ${g.amount.toLocaleString()}
                            </div>
                          </div>
                          {matched && detail.status !== "finalized" && (
                            <Button size="sm" variant="ghost" onClick={() => unmatch(g.gl_entry_id)} className="mt-1 h-6 px-2 text-xs">
                              <XCircle size={10} className="mr-1" /> Unmatch
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                    <Link2 size={12} /> Bank Transactions ({detail.bank_transactions?.length || 0})
                  </div>
                  <div className="space-y-1 max-h-96 overflow-y-auto">
                    {(detail.bank_transactions || []).map((b) => {
                      const matched = detail.matches.find((m) => m.bank_txn_id === b.id);
                      const isFinal = detail.status === "finalized";
                      return (
                        <div key={b.id} className={`text-xs p-2 rounded border ${matched ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10"}`}
                          data-testid={`bank-txn-${b.id}`}>
                          <div className="flex justify-between items-center gap-2">
                            <div className="flex-1 truncate">
                              <span className="text-muted-foreground">{b.date}</span> {b.description}
                            </div>
                            <div className={b.amount < 0 ? "text-rose-400 font-mono" : "text-emerald-500 font-mono"}>
                              ${b.amount.toLocaleString()}
                            </div>
                          </div>
                          {!matched && !isFinal && (
                            <div className="mt-1 flex gap-1 flex-wrap">
                              {(detail.gl_entries || []).filter((g) => !detail.matches.find((m) => m.gl_entry_id === g.gl_entry_id) && Math.abs(g.amount - b.amount) < 0.01).slice(0, 3).map((g) => (
                                <Button key={g.gl_entry_id} size="sm" variant="outline" className="h-6 px-2 text-xs"
                                  onClick={() => match(g.gl_entry_id, b.id)}
                                  data-testid={`match-${g.gl_entry_id}-${b.id}`}>
                                  <CheckCircle2 size={10} className="mr-1" /> Match ${g.amount}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                <Button onClick={finalize} disabled={detail.status === "finalized" || Math.abs(detail.variance) > 0.01} data-testid="finalize-recon">
                  {detail.status === "finalized" ? "Finalized" : "Finalize"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
